const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  variantChange: "variant-change",
  cartError: "cart-error",
};
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name"),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const sourceQty = html.querySelector("cart-items");
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble-mobile",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "change",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  async init() {
    const res = await fetch(window.Shopify.routes.root + "cart.js");
    const cart = await res.json();

    if (cart.items.length < 1) {
      return;
    }

    const shuffledCart = [...cart.items].sort(() => 0.5 - Math.random());
    const cartItems = shuffledCart.slice(0, 4);

    const cartRecommendations = cartItems.map((item) => {
      return new Promise((resolve, reject) => {
        fetch(
          `${window.Shopify.routes.root}recommendations/products?product_id=${
            item.product_id
          }&limit=${Math.ceil(
            10 / cartItems.length
          )}&section_id=cart-recommendations`
        )
          .then((res) => res.text())
          .then((html) => {
            const recommendationItems = new DOMParser()
              .parseFromString(html, "text/html")
              .querySelectorAll(".grid__item");
            resolve(recommendationItems);
          })
          .catch((error) => {
            reject(error);
          });
      });
    });

    Promise.all(cartRecommendations)
      .then((recommendations) => {
        const recommendationWrapper = this.querySelector(
          ".cart-recommendations"
        );
        const element = document.createElement("ul");
        element.className =
          "grid product-grid grid--4-col-desktop grid--2-col-tablet-down";

        const recommendationTitles = [];
        const allRecommendations = [];

        recommendations.map((recommendation) => {
          let recommendationUnique = [];

          if (recommendationTitles.length > 0) {
            recommendation.forEach((element) => {
              const elementTitle = element
                .querySelector(".card__heading a")
                .innerText.trim();

              if (recommendationTitles.indexOf(elementTitle) == -1) {
                recommendationUnique.push(element);
                recommendationTitles.push(elementTitle);
              }
            });
          } else {
            recommendationUnique = recommendation;
          }

          allRecommendations.push(Array.from(recommendationUnique));
        });

        const allRecommendationsFlat = allRecommendations.flat();
        const recommendationIndexes = [];

        if (allRecommendationsFlat.length < 4) {
          return;
        }

        while (recommendationIndexes.length < 4) {
          const number = Math.floor(
            Math.random() * allRecommendationsFlat.length
          );
          if (recommendationIndexes.indexOf(number) === -1) {
            recommendationIndexes.push(number);
          }
          if (recommendationIndexes.length == 4) {
            break;
          }
        }

        const recommendationsCartAttribute = [];

        allRecommendationsFlat.forEach((item, index) => {
          if (recommendationIndexes.indexOf(index) !== -1) {
            element.append(item);

            const productTitle = encodeURIComponent(
              item
                .querySelector(".card__information .card__heading span")
                .innerText.replace(/[\n\r]/g, "")
                .trim()
            );
            const productImage = encodeURIComponent(
              item.querySelector(".card__media .media img").getAttribute("src")
            );

            let productUrlRaw = item
              .querySelector(".card__information .card__heading a")
              .getAttribute("href");

            productUrlRaw = productUrlRaw.split("?");
            productUrlRaw = productUrlRaw[0];

            const productUrl = encodeURIComponent(
              `${productUrlRaw}?utm_source=bestellbestaetigungs-mail&utm_medium=mail-link&utm_campaign=upsell-produkte`
            );

            const attributeElement = `${productTitle}||${productImage}||${productUrl}`;

            if (
              recommendationsCartAttribute.length < 3 &&
              recommendationsCartAttribute.indexOf(attributeElement) == -1
            ) {
              recommendationsCartAttribute.push(attributeElement);
            }
          }
        });

        recommendationWrapper
          .querySelector("#cart-recommendations")
          .append(element);
        recommendationWrapper.classList.remove("hidden");

        const recommendationInput = document.createElement("input");

        recommendationInput.type = "hidden";
        recommendationInput.name = "attributes[_recommendations]";
        recommendationInput.value = recommendationsCartAttribute.join(",");

        document
          .querySelector(".cart__contents")
          .appendChild(recommendationInput);
      })
      .catch((error) => {
        console.log(error);
      });
  }
}

customElements.define("cart-recommendations", CartRecommendations);

class UpsellItem extends CartItems {
  constructor() {
    super();
    this.addUpsellButton = this.querySelector("button");
    this.addUpsellButton.addEventListener("click", this.addUpsell.bind(this));
  }
  enableLoadingButton() {
    this.addUpsellButton.setAttribute("aria-disabled", true);
    this.addUpsellButton.classList.add("loading");

    this.querySelectorAll(".loading-overlay__spinner").forEach((spinner) => {
      spinner.classList.remove("hidden");
    });
  }

  disableLoadingButton() {
    this.addUpsellButton.setAttribute("aria-disabled", false);
    this.addUpsellButton.classList.remove("loading");

    this.querySelectorAll(".loading-overlay__spinner").forEach((spinner) => {
      spinner.classList.add("hidden");
    });
  }

  addUpsell(e) {
    e.preventDefault();
    this.enableLoadingButton();

    const removeItems = this.addUpsellButton.dataset.contaningId
      .split(",")
      .filter((item) => item);
    const removeBody = JSON.stringify({
      updates: removeItems.reduce((prev, current) => {
        prev[current] = 0;
        return prev;
      }, {}),
    });

    fetch(`${routes.cart_update_url}.js`, {
      ...fetchConfig(),
      ...{ body: removeBody },
    }).then((response) => {
      /* Start customizations "enhanced-datalayer" */
      /* we need to send default formData like in product-form.js to get a more rich response */
      /* seems like a bug from Shopify, because based on the content-type of the request the result is differently */
      const config = fetchConfig("javascript");
      const formData = new FormData();

      const { id } = this.addUpsellButton.dataset;
      formData.append("id", id);
      formData.append("quantity", 1);
      formData.append(
        "sections",
        this.getSectionsToRender()
          .map((section) => section.section)
          .join(",")
      );
      formData.append("sections_url", window.location.pathname);

      config.body = formData;
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      /* End customizations "enhanced-datalayer" */

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);
          this.classList.toggle("is-empty", parsedState.item_count === 0);
          const cartFooter = document.getElementById("main-cart-footer");

          if (cartFooter)
            cartFooter.classList.toggle(
              "is-empty",
              parsedState.item_count === 0
            );

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document
                .getElementById(section.id)
                .querySelector(section.selector) ||
              document.getElementById(section.id);

            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          this.disableLoadingButton();
        })
        .catch(() => {
          document.getElementById("cart-errors").textContent =
            window.cartStrings.error;
        });
    });
  }
}
customElements.define("upsell-item", UpsellItem);

class CartProductUpsell extends HTMLElement {
  constructor() {
    super();
    this.lineItems = this.dataset.products.split(",");
    this.loadUpsell();
  }

  loadUpsell() {
    console.log(this.lineItems);
    fetch(
      `${window.Shopify.routes.root}recommendations/products?product_id=${this.lineItems[0]}&limit=1&section_id=cart-item-upsell`
    )
      .then((response) => response.text())
      .then((html) => {
        const tr = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector("#shopify-section-cart-item-upsell").innerHTML;
        this.querySelector("table tbody").innerHTML += tr;
      });
  }
}

customElements.define("cart-product-upsell", CartProductUpsell);
